import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import {
  Bell, Search, ArrowUpDown, ArrowUp, ArrowDown, CheckCheck, Check,
  RotateCcw, X, RefreshCw, Inbox,
} from 'lucide-react';
import notificationService from '../../services/notificationService';
import {
  formatEventType, eventTypeColor, siteOf, participantOf, studyOf,
} from './notificationNavigation';
import NotificationDetailModal from './NotificationDetailModal';

const PAGE_SIZE = 25;

const getSelectedSponsor = () => {
  try {
    const raw = localStorage.getItem('selectedSponsor');
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

// Column key -> backend ordering field.
const ORDER_FIELD = {
  is_read: 'read_at',
  event_type_key: 'event_type__key',
  title: 'title',
  site: 'site__site_code',
  participant: 'participant__participant_number',
  study: 'study_id',
  created_at: 'created_at',
};

// Percentage widths + table-fixed keep the table inside the viewport;
// the unspecified Notification column absorbs the remaining ~33%.
const COLUMNS = [
  { key: 'is_read', label: 'Status', sortable: true, width: 'w-[8%]' },
  { key: 'event_type_key', label: 'Type', sortable: true, width: 'w-[12%]' },
  { key: 'title', label: 'Notification', sortable: true },
  { key: 'site', label: 'Site', sortable: true, width: 'w-[8%]' },
  { key: 'participant', label: 'Participant', sortable: true, width: 'w-[11%]' },
  { key: 'study', label: 'Study', sortable: true, width: 'w-[9%]' },
  { key: 'created_at', label: 'Date', sortable: true, width: 'w-[13%]' },
  { key: 'actions', label: '', sortable: false, width: 'w-[6%]' },
];

const Select = ({ label, value, onChange, options }) => {
  const id = `nf-${label.toLowerCase()}`;
  return (
    <div className="flex flex-col">
      <label htmlFor={id} className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{label}</label>
      <select
        id={id}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="text-sm border border-gray-200 rounded-lg px-3 py-2 bg-white focus:outline-none focus:ring-2 focus:ring-blue-400 min-w-[9rem]"
      >
        <option value="">All</option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
};

const NotificationsPage = () => {
  const sponsor = getSelectedSponsor();
  const sponsorId = sponsor?.id || null;

  const [rows, setRows] = useState([]);
  const [detail, setDetail] = useState(null);
  const [count, setCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [facets, setFacets] = useState({ event_types: [], sites: [], participants: [], studies: [] });

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState({ type: '', status: '', site: '', participant: '', study: '' });
  const [sort, setSort] = useState({ key: 'created_at', dir: 'desc' });
  const [page, setPage] = useState(1);

  // Debounce the search box.
  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput.trim()), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  // Reset to page 1 whenever the query changes.
  useEffect(() => { setPage(1); }, [filters, search, sort, sponsorId]);

  const buildParams = useCallback(() => {
    const params = { page, page_size: PAGE_SIZE };
    if (sponsorId) params.sponsor = sponsorId;
    if (search) params.search = search;
    if (filters.type) params.event_type_key = filters.type;
    if (filters.status) params.is_read = filters.status === 'read';
    if (filters.site) params.site = filters.site;
    if (filters.participant) params.participant = filters.participant;
    if (filters.study) params.study = filters.study;
    const field = ORDER_FIELD[sort.key] || 'created_at';
    const primary = `${sort.dir === 'desc' ? '-' : ''}${field}`;
    // `-id` tiebreaker keeps pages from overlapping when sort values tie.
    params.ordering = field === 'id' ? primary : `${primary},-id`;
    return params;
  }, [page, sponsorId, search, filters, sort]);

  const fetchPage = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await notificationService.getNotifications(buildParams());
      setRows(data.results || []);
      setCount(typeof data.count === 'number' ? data.count : (data.results || []).length);
    } catch (e) {
      setError('Could not load notifications.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [buildParams]);

  const fetchUnread = useCallback(async () => {
    setUnreadCount(await notificationService.getUnreadCount(sponsorId));
  }, [sponsorId]);

  const fetchFacets = useCallback(async () => {
    setFacets(await notificationService.getFacets(sponsorId));
  }, [sponsorId]);

  useEffect(() => { fetchPage(); }, [fetchPage]);
  useEffect(() => { fetchUnread(); fetchFacets(); }, [fetchUnread, fetchFacets]);

  const options = useMemo(() => ({
    type: (facets.event_types || []).map((k) => ({ value: k, label: formatEventType(k) })),
    site: (facets.sites || []).map((s) => ({ value: String(s.value), label: s.label })),
    participant: (facets.participants || []).map((p) => ({ value: String(p.value), label: `P${p.label}` })),
    study: (facets.studies || []).map((s) => ({ value: String(s.value), label: s.label || `Study ${s.value}` })),
  }), [facets]);

  const totalPages = Math.max(1, Math.ceil(count / PAGE_SIZE));
  const activeFilters = Object.values(filters).some(Boolean) || search;

  const toggleSort = (key) => {
    setSort((s) => (s.key === key
      ? { key, dir: s.dir === 'asc' ? 'desc' : 'asc' }
      : { key, dir: key === 'created_at' ? 'desc' : 'asc' }));
  };

  const setReadState = async (n, read) => {
    try {
      if (read) await notificationService.markAsRead([n.id]);
      else await notificationService.markAsUnread([n.id]);
      setRows((prev) => prev.map((x) => (x.id === n.id ? { ...x, is_read: read } : x)));
      fetchUnread();
    } catch { /* leave row as-is on failure */ }
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationService.markAllAsRead(sponsorId);
      setRows((prev) => prev.map((x) => ({ ...x, is_read: true })));
      fetchUnread();
    } catch { /* no-op */ }
  };

  const handleRowClick = (n) => {
    if (!n.is_read) setReadState(n, true);
    setDetail(n);
  };

  const clearFilters = () => {
    setFilters({ type: '', status: '', site: '', participant: '', study: '' });
    setSearchInput('');
  };

  const refresh = () => { fetchPage(); fetchUnread(); fetchFacets(); };

  // Stacked date/time keeps the Date column narrow enough to fit on one screen.
  const formatDay = (d) => new Date(d).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
  const formatTime = (d) => new Date(d).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  const SortIcon = ({ col }) => {
    if (!col.sortable) return null;
    if (sort.key !== col.key) return <ArrowUpDown size={13} className="text-gray-300 inline ml-1" />;
    return sort.dir === 'asc'
      ? <ArrowUp size={13} className="text-blue-500 inline ml-1" />
      : <ArrowDown size={13} className="text-blue-500 inline ml-1" />;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-blue-50 text-blue-600"><Bell size={22} /></div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Notifications</h1>
            <p className="text-sm text-gray-500">
              {sponsor?.name ? <span className="font-medium text-gray-700">{sponsor.name}</span> : 'All sponsors'}
              {' · '}{count}{activeFilters ? ' matching' : ' total'}
              {unreadCount > 0 && <span className="text-blue-600 font-medium"> · {unreadCount} unread</span>}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={refresh}
            className="flex items-center gap-1.5 text-sm text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50"
          >
            <RefreshCw size={15} /> Refresh
          </button>
          {unreadCount > 0 && (
            <button
              onClick={handleMarkAllRead}
              className="flex items-center gap-1.5 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg px-3 py-2"
            >
              <CheckCheck size={15} /> Mark all read
            </button>
          )}
        </div>
      </div>

      {/* Toolbar */}
      <div className="bg-white rounded-xl shadow-sm ring-1 ring-black/5 p-4 mb-4">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex flex-col flex-1 min-w-[14rem]">
            <label htmlFor="nf-search" className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">Search</label>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                id="nf-search"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search title or body…"
                className="w-full text-sm border border-gray-200 rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-400"
              />
            </div>
          </div>
          <Select label="Type" value={filters.type} onChange={(v) => setFilters((f) => ({ ...f, type: v }))} options={options.type} />
          <Select
            label="Status" value={filters.status}
            onChange={(v) => setFilters((f) => ({ ...f, status: v }))}
            options={[{ value: 'unread', label: 'Unread' }, { value: 'read', label: 'Read' }]}
          />
          <Select label="Site" value={filters.site} onChange={(v) => setFilters((f) => ({ ...f, site: v }))} options={options.site} />
          <Select label="Participant" value={filters.participant} onChange={(v) => setFilters((f) => ({ ...f, participant: v }))} options={options.participant} />
          <Select label="Study" value={filters.study} onChange={(v) => setFilters((f) => ({ ...f, study: v }))} options={options.study} />
          {activeFilters && (
            <button onClick={clearFilters} className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-800 px-2 py-2">
              <X size={14} /> Clear
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm ring-1 ring-black/5 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm table-fixed">
            <thead>
              <tr className="bg-gray-50 text-gray-500 text-left">
                {COLUMNS.map((col) => (
                  <th
                    key={col.key}
                    onClick={() => col.sortable && toggleSort(col.key)}
                    className={`px-3 py-3 font-medium text-xs uppercase tracking-wide whitespace-nowrap overflow-hidden ${col.width || ''} ${col.sortable ? 'cursor-pointer select-none hover:text-gray-700' : ''}`}
                  >
                    {col.label}<SortIcon col={col} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={COLUMNS.length} className="px-4 py-16 text-center">
                  <div className="animate-spin rounded-full h-7 w-7 border-b-2 border-blue-500 mx-auto" />
                  <p className="text-gray-500 mt-3">Loading notifications…</p>
                </td></tr>
              ) : error ? (
                <tr><td colSpan={COLUMNS.length} className="px-4 py-16 text-center text-red-500">{error}</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={COLUMNS.length} className="px-4 py-16 text-center">
                  <Inbox size={28} className="mx-auto text-gray-300" />
                  <p className="text-gray-500 mt-3">{activeFilters ? 'No notifications match your filters' : 'No notifications yet'}</p>
                </td></tr>
              ) : rows.map((n) => {
                const color = eventTypeColor(n.event_type_key);
                return (
                  <motion.tr
                    key={n.id}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onClick={() => handleRowClick(n)}
                    className={`cursor-pointer hover:bg-blue-50/40 transition-colors ${!n.is_read ? 'bg-blue-50/60' : ''}`}
                  >
                    <td className="px-3 py-3">
                      {n.is_read ? (
                        <span className="inline-flex items-center gap-1 text-xs text-gray-400"><Check size={13} /> Read</span>
                      ) : (
                        <span className="inline-flex items-center gap-1.5 text-xs font-medium text-blue-600">
                          <span className="w-2 h-2 rounded-full bg-blue-500" /> New
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-3 overflow-hidden">
                      <span className={`inline-block max-w-full truncate px-2 py-0.5 rounded text-xs font-medium ${color.badge}`}>
                        {formatEventType(n.event_type_key)}
                      </span>
                    </td>
                    <td className="px-3 py-3 overflow-hidden">
                      <p className={`${!n.is_read ? 'font-semibold text-gray-900' : 'text-gray-700'} truncate`}>{n.title}</p>
                      <p className="text-xs text-gray-400 truncate">{n.body}</p>
                    </td>
                    <td className="px-3 py-3 text-gray-600 truncate">{siteOf(n) || '—'}</td>
                    <td className="px-3 py-3 text-gray-600 truncate">{participantOf(n) || '—'}</td>
                    <td className="px-3 py-3 text-gray-600 truncate">{studyOf(n) || '—'}</td>
                    <td className="px-3 py-3 text-gray-500">
                      <div className="whitespace-nowrap">{formatDay(n.created_at)}</div>
                      <div className="text-xs text-gray-400 whitespace-nowrap">{formatTime(n.created_at)}</div>
                    </td>
                    <td className="px-3 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => setReadState(n, !n.is_read)}
                        title={n.is_read ? 'Mark unread' : 'Mark read'}
                        className="text-gray-400 hover:text-blue-600 p-1.5 rounded-lg hover:bg-gray-100"
                      >
                        {n.is_read ? <RotateCcw size={15} /> : <Check size={15} />}
                      </button>
                    </td>
                  </motion.tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {!loading && count > 0 && (
          <div className="flex items-center justify-between px-4 py-3 border-t border-gray-100 text-sm text-gray-500">
            <span>
              {(page - 1) * PAGE_SIZE + 1}–{Math.min(page * PAGE_SIZE, count)} of {count}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >Prev</button>
              <span className="px-2">Page {page} / {totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="px-3 py-1.5 rounded-lg border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >Next</button>
            </div>
          </div>
        )}
      </div>

      {detail && (
        <NotificationDetailModal notification={detail} onClose={() => setDetail(null)} />
      )}
    </div>
  );
};

export default NotificationsPage;
